import { TreatmentAppointmentOutcomeFields } from "./TreatmentAppointmentOutcomeFields";
import { invalidateMatterAndSearchQueries } from "@/lib/data/react-query/search/invalidation";
import { MatterTreatmentPlansCard } from "@/components/features";
import { AddOrEditFileReferenceSlideover } from "@/components/features/matter/file-repo/AddOrEditFileReferenceSlideover";
import {
  ActionConfirmationModal,
  Button,
  Calendar as CalendarPicker,
  Checkbox,
  DateFormField,
  Form,
  FormItem,
  FormSubmitButton,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  SwitchFormField,
  Textarea,
  TextAreaFormField,
  Typography,
} from "@/components/ui";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { DateField } from "@/components/ui/FormFields/DateFormField/DateField";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { MultiselectFormField } from "@/components/ui/FormFields/MultiselectFormField/MultiselectFormField";
import { TimeFormField } from "@/components/ui/FormFields/TimeFormField/TimeFormField";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/shadcn/Dialog/Dialog";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { useUser } from "@/components/UserProvider";
import { useMedicalTreatmentViewModel } from "@/hooks/matter-view-models/use-medical-treatment-view-model";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import { useDeleteMatterContactConnection } from "@/lib/data/react-query/matters/matter-contact-connection";
import {
  useCreateMatterFileReference,
  useDeleteMatterFileReference,
} from "@/lib/data/react-query/matters/matter-file";
import {
  useCreateMedicalTreatmentAppointment,
  useDeleteMedicalTreatmentAppointment,
  useDeleteMedicalTreatmentAppointmentConnection,
  useUpdateMedicalTreatmentAppointment,
  useUpdateMedicalTreatmentAppointmentConnections,
} from "@/lib/data/react-query/matters/medical-treatment-appointment";
import {
  useCreateMedicalTreatmentBill,
  useUpdateMedicalTreatmentBill,
} from "@/lib/data/react-query/matters/medical-treatment-bill";
import {
  useCreateMedicalTreatmentPlan,
  useUpdateMedicalTreatmentPlan,
} from "@/lib/data/react-query/matters/medical-treatment-plan";
import {
  useCreateMedicalTreatmentRecord,
  useUpdateMedicalTreatmentRecord,
} from "@/lib/data/react-query/matters/medical-treatment-record";
import { MedicalTreatmentAppointmentSchema } from "@/lib/data/schemas";
import { CONTACT_ORGANIZATION_MEDICAL_PROVIDER_SPECIALIZATIONS } from "@/lib/data/static/contactStatics";
import {
  MEDICAL_TREATMENT_LIEN_STATUS_OPTIONS,
  MEDICAL_TREATMENT_RECORD_SELECTABLE_STATUS_OPTIONS,
} from "@/lib/data/static/matterStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import {
  SLIDEOVER_CONFIRM_CLOSE_DESCRIPTION,
  SLIDEOVER_DESCRIPTION,
  SLIDEOVER_TITLE,
} from "@/lib/data/static/slideoverStatics";
import { syncSingleContactConnection } from "@/lib/utils/connections/item-contact-connections";
import {
  getPlanFulfillerConnection,
  PLAN_FULFILLER_CONNECTION_TYPE,
} from "@/lib/utils/connections/medical-treatment-subitem-contact-connections";
import {
  type MatterFile,
  type MatterSlideoverInitialTabsType,
  type MedicalTreatmentAppointment,
  type MedicalTreatmentBill,
  type MedicalTreatmentRecord,
} from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { formatDate, trimText } from "@/lib/utils/format";
import { mergeInitialData } from "@/lib/utils/initialDataUtils";
import { mergeFilesAndReferenceId } from "@/lib/utils/matterFile";
import {
  assertInlineTreatmentBillFormValid,
  canSetMedicalTreatmentBillLien,
  InlineTreatmentBillValidationError,
  validateInlineTreatmentBillForm,
  type InlineTreatmentBillFieldErrorKey,
  type InlineTreatmentBillFieldErrors,
} from "@/lib/utils/matters/medical-treatment-bill-utils";
import {
  validateInlineTreatmentRecordForm,
  type InlineTreatmentRecordFieldErrorKey,
  type InlineTreatmentRecordFieldErrors,
} from "@/lib/utils/matters/medical-treatment-record-utils";
import { useModals } from "@/providers/ModalProvider";
import { zodResolver } from "@hookform/resolvers/zod";
import { parseDate } from "@internationalized/date";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { difference, isNil, omit, omitBy } from "lodash";
import {
  Calendar,
  CalendarIcon,
  ChevronDown,
  FileText,
  Link2,
  Plus,
  Sparkles,
  Upload,
  XIcon,
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";
import {
  useButton,
  useDatePicker,
  useInteractOutside,
  type DateValue,
} from "react-aria";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { NumericFormat } from "react-number-format";
import { useDatePickerState } from "react-stately";
import { z } from "zod";

interface AddOrEditTreatmentAppointmentSlideoverProps {
  title: string;
  description?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  editing?: boolean;
  initialTab?: MatterSlideoverInitialTabsType;
  matterId: string;
  appointmentId?: string;
  treatmentId?: string;
  initialData?: Partial<MedicalTreatmentAppointment>;
}

type NewRecordFormValues = {
  name: string;
  status:
    | (typeof MEDICAL_TREATMENT_RECORD_SELECTABLE_STATUS_OPTIONS)[number]
    | "";
  requestDate: string;
  receivedDate: string;
  rejectedDate: string;
  rejectedReason: string;
};

type NewBillFormValues = {
  recordLabel: string;
  recordStatus:
    | (typeof MEDICAL_TREATMENT_RECORD_SELECTABLE_STATUS_OPTIONS)[number]
    | "";
  recordRequestDate: string;
  recordReceivedDate: string;
  originalBill: string;
  goalAmount: string;
  adjustedBill: string;
  negotiatedAmount: string;
  clientPaid: string;
  insurancePaid: string;
  medpayPipPaid: string;
  lien: string;
  recordRejectedDate: string;
};

const PENDING_FILE_STAGING_PREFIX = "pending-file-staging-";

const isAiGeneratedBill = (bill: unknown) => {
  if (!bill || typeof bill !== "object") {
    return false;
  }

  const candidate = bill as {
    aiStatus?: unknown;
    aiAffectedFields?: unknown[] | null;
  };

  return Boolean(candidate.aiStatus || candidate.aiAffectedFields?.length);
};

const appointmentFormSchema = MedicalTreatmentAppointmentSchema.pick({
  date: true,
  dateEnd: true,
  multiday: true,
  time: true,
  type: true,
  description: true,
  outcome: true,
  missedReason: true,
  billNotApplicable: true,
  recordNotApplicable: true,
})
  .extend({
    type: z
      .array(z.enum(CONTACT_ORGANIZATION_MEDICAL_PROVIDER_SPECIALIZATIONS))
      .min(1, "At least one appointment type is required"),
    medicalTreatmentRecordId: z.array(z.string()).optional(),
    medicalTreatmentBillId: z.array(z.string()).optional(),
  })
  .refine((values) => !values.multiday || Boolean(values.dateEnd), {
    path: ["dateEnd"],
    message: "End date is required for a date range",
  });

const defaultFormValues = {
  date: "",
  dateEnd: null,
  multiday: false,
  time: "",
  type: [],
  description: "",
  outcome: "" as const,
  missedReason: "" as const,
  billNotApplicable: false,
  recordNotApplicable: false,
  medicalTreatmentRecordId: [] as string[],
  medicalTreatmentBillId: [] as string[],
};

const InlineFieldLabel = ({
  children,
  required,
}: {
  children: ReactNode;
  required?: boolean;
}) => (
  <Label className="text-muted-foreground text-xs font-medium">
    {children}
    {required && <span className="text-destructive"> *</span>}
  </Label>
);

const InlineFieldError = ({ error }: { error?: string }) =>
  error ? <p className="text-destructive text-xs">{error}</p> : null;

const InlineTextField = ({
  label,
  value,
  onChange,
  type = "text",
  required,
  error,
}: {
  label: string;
  value?: string | number | null;
  onChange: (value: string) => void;
  type?: InputHTMLAttributes<HTMLInputElement>["type"];
  required?: boolean;
  error?: string;
}) => (
  <FormItem className="space-y-1">
    <InlineFieldLabel required={required}>{label}</InlineFieldLabel>
    <Input
      type={type}
      value={value ?? ""}
      aria-invalid={Boolean(error)}
      className={cn(error && "border-destructive")}
      onChange={(e) => {
        onChange(e.target.value);
      }}
    />
    <InlineFieldError error={error} />
  </FormItem>
);

const parseInlineDateValue = (value?: string | null) => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }

  try {
    return parseDate(value);
  } catch {
    return null;
  }
};

const InlineDateField = ({
  label,
  value,
  onChange,
  required,
  error,
}: {
  label: string;
  value?: string | null;
  onChange: (value: string) => void;
  required?: boolean;
  error?: string;
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const parsedValue = parseInlineDateValue(value);

  const state = useDatePickerState({
    value: parsedValue,
    onChange: (dateValue: DateValue | null) => {
      onChange(dateValue?.toString() ?? "");
    },
  });
  const {
    groupProps,
    fieldProps,
    buttonProps: datePickerButtonProps,
    dialogProps,
    calendarProps,
  } = useDatePicker({ "aria-label": label }, state, ref);
  const { buttonProps } = useButton(datePickerButtonProps, buttonRef);

  useInteractOutside({
    ref: contentRef,
    onInteractOutside: () => {
      setOpen(false);
    },
  });

  return (
    <FormItem className="space-y-1">
      <InlineFieldLabel required={required}>{label}</InlineFieldLabel>
      <div
        {...groupProps}
        ref={ref}
        className={cn(
          groupProps.className,
          "ring-offset-background focus-within:ring-ring relative flex items-center rounded-md focus-within:ring-2 focus-within:ring-offset-2",
          error && "ring-destructive focus-within:ring-destructive",
        )}
      >
        <DateField
          {...fieldProps}
          onChange={(dateValue) => {
            fieldProps.onChange?.(dateValue);
            onChange(dateValue?.toString() ?? "");
          }}
        />
        {value && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-12 mx-2 my-1 h-4 w-4 cursor-pointer"
            onClick={(event) => {
              event.stopPropagation();
              fieldProps.onChange?.(null);
              onChange("");
            }}
            type="button"
          >
            <XIcon className="text-muted-foreground absolute mx-2 my-1 h-3 w-3 cursor-pointer" />
          </Button>
        )}
        <Popover modal open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              {...buttonProps}
              variant="outline"
              size="icon"
              className="border-input h-10 shrink-0 self-stretch rounded-l-none"
              ref={buttonRef}
              onClick={() => {
                setOpen(true);
              }}
              type="button"
            >
              <CalendarIcon className="h-5 w-5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent ref={contentRef} className="w-full">
            <div {...dialogProps} className="space-y-3">
              <CalendarPicker
                {...calendarProps}
                onChange={(dateValue) => {
                  calendarProps.onChange?.(dateValue);
                  onChange(dateValue.toString());
                }}
              />
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <InlineFieldError error={error} />
    </FormItem>
  );
};

const InlineCurrencyField = ({
  label,
  value,
  onChange,
  required,
  error,
}: {
  label: string;
  value?: string | number | null;
  onChange: (value: string) => void;
  required?: boolean;
  error?: string;
}) => (
  <FormItem className="space-y-1">
    <InlineFieldLabel required={required}>{label}</InlineFieldLabel>
    <div className="relative">
      <span className="text-muted-foreground absolute inset-y-0 left-0 flex items-center pl-3 text-sm">
        $
      </span>
      <NumericFormat
        className={cn("pl-8", error && "border-destructive")}
        customInput={Input}
        decimalScale={2}
        thousandSeparator=","
        value={value ?? ""}
        onValueChange={({ value: nextValue }) => {
          onChange(nextValue);
        }}
      />
    </div>
    <InlineFieldError error={error} />
  </FormItem>
);

type InlineSelectFieldProps<T extends string> = {
  label: string;
  value?: T | "";
  options: readonly T[];
} & (
  | { clearable?: false; onChange: (value: T) => void }
  | { clearable: true; onChange: (value: T | "") => void }
);

const InlineSelectField = <T extends string>({
  label,
  value,
  options,
  onChange,
  clearable,
  error,
}: InlineSelectFieldProps<T> & { error?: string }) => (
  <FormItem className="space-y-1">
    <InlineFieldLabel>{label}</InlineFieldLabel>
    <Select
      value={clearable ? value || "__none__" : (value ?? "")}
      onValueChange={(nextValue) => {
        if (clearable && nextValue === "__none__") {
          (onChange as (value: T | "") => void)("");
          return;
        }
        onChange(nextValue as T);
      }}
    >
      <SelectTrigger className={cn(error && "border-destructive")}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {clearable && <SelectItem value="__none__">None</SelectItem>}
        {options.map((option) => (
          <SelectItem key={option} value={option}>
            {option}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
    <InlineFieldError error={error} />
  </FormItem>
);

const InlineTextareaField = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value?: string | null;
  onChange: (value: string) => void;
}) => (
  <FormItem className="space-y-1">
    <InlineFieldLabel>{label}</InlineFieldLabel>
    <Textarea
      className="min-h-[60px] resize-none"
      value={value ?? ""}
      onChange={(e) => {
        onChange(e.target.value);
      }}
    />
  </FormItem>
);

export const AddOrEditTreatmentAppointmentSlideover = ({
  title,
  open,
  setOpen,
  editing,
  matterId,
  appointmentId,
  treatmentId,
  initialData,
}: AddOrEditTreatmentAppointmentSlideoverProps) => {
  const queryClient = useQueryClient();
  const { hasActionPermission } = useUser();
  const canCreateAppointmentConnections = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterMedicalTreatmentAppointmentConnection,
    ACTION_PERMISSION_VALUES.create,
  );
  const canDeleteAppointmentConnections = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterMedicalTreatmentAppointmentConnection,
    ACTION_PERMISSION_VALUES.delete,
  );
  const { updateSlideoverPayload } = useModals();
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [rightPanel, setRightPanel] = useState<"records" | "bills">("records");
  const [showAttachExistingRecords, setShowAttachExistingRecords] =
    useState(false);
  const [showAttachExistingBills, setShowAttachExistingBills] = useState(false);
  const [expandedBillId, setExpandedBillId] = useState<string | null>(null);
  const [expandedRecordId, setExpandedRecordId] = useState<string | null>(null);
  const [billDrafts, setBillDrafts] = useState<
    Partial<Record<string, Partial<MedicalTreatmentBill>>>
  >({});
  const [recordDrafts, setRecordDrafts] = useState<
    Partial<Record<string, Partial<MedicalTreatmentRecord>>>
  >({});
  const [existingRecordFiles, setExistingRecordFiles] = useState<
    Record<string, MatterFile[]>
  >({});
  const [existingBillFiles, setExistingBillFiles] = useState<
    Record<string, MatterFile[]>
  >({});
  const [activeFileReferenceTarget, setActiveFileReferenceTarget] = useState<{
    itemId: string;
    itemType: "bill" | "record";
  } | null>(null);
  const [pendingFileReferenceDeletes, setPendingFileReferenceDeletes] =
    useState<string[]>([]);
  const [showNewRecordForm, setShowNewRecordForm] = useState(false);
  const [newRecordTempId, setNewRecordTempId] = useState<string | null>(null);
  const [newRecord, setNewRecord] = useState({
    name: "",
    status: "Requested" as NewRecordFormValues["status"],
    requestDate: dayjs().format("YYYY-MM-DD"),
    receivedDate: "",
    rejectedDate: "",
    rejectedReason: "",
  });
  const [showNewBillForm, setShowNewBillForm] = useState(false);
  const [newBillTempId, setNewBillTempId] = useState<string | null>(null);
  const [newBill, setNewBill] = useState({
    recordLabel: "",
    recordStatus: "Requested" as NewBillFormValues["recordStatus"],
    recordRequestDate: dayjs().format("YYYY-MM-DD"),
    recordReceivedDate: "",
    originalBill: "",
    goalAmount: "",
    adjustedBill: "",
    negotiatedAmount: "",
    clientPaid: "",
    insurancePaid: "",
    medpayPipPaid: "",
    lien: "",
    recordRejectedDate: "",
  });
  const [newBillErrors, setNewBillErrors] =
    useState<InlineTreatmentBillFieldErrors>({});
  const [newRecordErrors, setNewRecordErrors] =
    useState<InlineTreatmentRecordFieldErrors>({});
  const [billFieldErrors, setBillFieldErrors] = useState<
    Record<string, InlineTreatmentBillFieldErrors>
  >({});
  const [localPlanItems, setLocalPlanItems] = useState<
    { id: string; description: string }[]
  >([]);
  const [locallyCompletedPlanIds, setLocallyCompletedPlanIds] = useState<
    string[]
  >([]);
  const [isSubmittingWorkflow, setIsSubmittingWorkflow] = useState(false);
  const createAppointment = useCreateMedicalTreatmentAppointment({
    suppressSuccessToast: true,
    suppressQueryInvalidation: true,
  });
  const updateAppointment = useUpdateMedicalTreatmentAppointment({
    suppressSuccessToast: true,
    suppressQueryInvalidation: true,
  });
  const deleteAppointment = useDeleteMedicalTreatmentAppointment();
  const updateAppointmentConnections =
    useUpdateMedicalTreatmentAppointmentConnections({
      suppressQueryInvalidation: true,
    });
  const deleteMedicalTreatmentAppointmentConnection =
    useDeleteMedicalTreatmentAppointmentConnection({
      suppressQueryInvalidation: true,
    });
  const updateBill = useUpdateMedicalTreatmentBill({
    suppressSuccessToast: true,
    suppressQueryInvalidation: true,
  });
  const updateRecord = useUpdateMedicalTreatmentRecord({
    suppressSuccessToast: true,
    suppressQueryInvalidation: true,
  });
  const createRecord = useCreateMedicalTreatmentRecord({
    suppressSuccessToast: true,
    suppressQueryInvalidation: true,
  });
  const createBill = useCreateMedicalTreatmentBill({
    suppressSuccessToast: true,
    suppressQueryInvalidation: true,
  });
  const createPlan = useCreateMedicalTreatmentPlan({
    suppressSuccessToast: true,
    suppressQueryInvalidation: true,
  });
  const updatePlan = useUpdateMedicalTreatmentPlan({
    suppressSuccessToast: true,
    suppressQueryInvalidation: true,
  });
  const deleteMatterContactConnection = useDeleteMatterContactConnection({
    showSuccessToast: false,
  });
  const createMatterFileReference = useCreateMatterFileReference();
  const deleteMatterFileReference = useDeleteMatterFileReference();

  const isFormLoading =
    isSubmittingWorkflow ||
    createAppointment.isPending ||
    updateAppointment.isPending ||
    updateAppointmentConnections.isPending ||
    deleteAppointment.isPending ||
    deleteMedicalTreatmentAppointmentConnection.isPending ||
    updateBill.isPending ||
    updateRecord.isPending ||
    createRecord.isPending ||
    createBill.isPending ||
    createPlan.isPending ||
    updatePlan.isPending ||
    deleteMatterContactConnection.isPending ||
    createMatterFileReference.isPending ||
    deleteMatterFileReference.isPending;

  const { data: matterData } = useSuspenseQuery(
    retrieveMatterQueryOptions(matterId),
  );

  const matter = matterData.data;

  const selectedAppointment = appointmentId
    ? matter.medicalTreatmentAppointments.find(
        (appointment) => appointment.id === appointmentId,
      )
    : undefined;
  const appointmentInitialValues = mergeInitialData(
    selectedAppointment,
    initialData,
  );

  const selectedTreatment = treatmentId
    ? matter.medicalTreatments.find((treatment) => treatment.id === treatmentId)
    : undefined;

  const completeMedicalTreatments = useMedicalTreatmentViewModel(matter);
  const completeTreatment = completeMedicalTreatments.find(
    (t) => t.id === selectedTreatment?.id,
  );
  const recordsNotApplicable = Boolean(
    completeTreatment?.recordNotApplicable ??
      selectedTreatment?.recordNotApplicable,
  );
  const billsNotApplicable = Boolean(
    completeTreatment?.billNotApplicable ??
      selectedTreatment?.billNotApplicable,
  );
  const appointmentWithConnections = useMemo(() => {
    return completeTreatment?.appointments.find(
      (a) => a.id === appointmentInitialValues?.id,
    );
  }, [completeTreatment, appointmentInitialValues?.id]);
  const currentProviderConnection = completeTreatment?.provider;

  const syncPlanFulfillerConnection = async (planId: string) => {
    const plan = matter.medicalTreatmentPlans.find(
      (item) => item.id === planId,
    );
    const providerContactId = currentProviderConnection?.targetObjectId;

    if (!providerContactId) {
      return;
    }

    await syncSingleContactConnection({
      matterId: matter.id,
      currentConnections: plan?.connections,
      connectionType: PLAN_FULFILLER_CONNECTION_TYPE,
      current: getPlanFulfillerConnection(plan),
      desiredContactId: providerContactId,
      sourceObjectId: planId,
      sourceObjectType: "matter_medical_treatment_plan",
    });
  };

  const appointmentForm = useForm<z.infer<typeof appointmentFormSchema>>({
    resolver: zodResolver(appointmentFormSchema),
    values:
      editing && (appointmentWithConnections || appointmentInitialValues)
        ? {
            date:
              (appointmentWithConnections
                ? appointmentWithConnections.date
                : appointmentInitialValues?.date) ?? "",
            dateEnd:
              appointmentWithConnections?.dateEnd ??
              appointmentInitialValues?.dateEnd ??
              null,
            multiday:
              appointmentWithConnections?.multiday ??
              appointmentInitialValues?.multiday ??
              false,
            time:
              (appointmentWithConnections
                ? appointmentWithConnections.time
                : appointmentInitialValues?.time) ?? "",
            type:
              (appointmentWithConnections
                ? appointmentWithConnections.type
                : appointmentInitialValues?.type) ?? [],
            description:
              (appointmentWithConnections
                ? appointmentWithConnections.description
                : appointmentInitialValues?.description) ?? "",
            outcome:
              appointmentWithConnections?.outcome ??
              appointmentInitialValues?.outcome ??
              "",
            missedReason:
              appointmentWithConnections?.missedReason ??
              appointmentInitialValues?.missedReason ??
              "",
            billNotApplicable:
              appointmentWithConnections?.billNotApplicable ??
              appointmentInitialValues?.billNotApplicable ??
              false,
            recordNotApplicable:
              appointmentWithConnections?.recordNotApplicable ??
              appointmentInitialValues?.recordNotApplicable ??
              false,
            medicalTreatmentRecordId:
              appointmentWithConnections &&
              appointmentWithConnections.records.map(
                (r: MedicalTreatmentRecord) => r.id,
              ),
            medicalTreatmentBillId:
              appointmentWithConnections &&
              appointmentWithConnections.bills.map(
                (b: MedicalTreatmentBill) => b.id,
              ),
          }
        : defaultFormValues,
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });
  const hasPendingExistingFileChanges =
    Object.values(existingBillFiles).some((files) => files.length > 0) ||
    Object.values(existingRecordFiles).some((files) => files.length > 0) ||
    pendingFileReferenceDeletes.length > 0;
  const today = dayjs().format("YYYY-MM-DD");
  const hasBillOrRecordDraftChanges =
    Object.keys(billDrafts).length > 0 ||
    Object.keys(recordDrafts).length > 0 ||
    hasPendingExistingFileChanges;
  const hasUnsavedChanges =
    appointmentForm.formState.isDirty ||
    hasBillOrRecordDraftChanges ||
    localPlanItems.length > 0 ||
    locallyCompletedPlanIds.length > 0 ||
    showNewRecordForm ||
    showNewBillForm;

  const resetLocalWorkflowState = () => {
    setExistingBillFiles({});
    setExistingRecordFiles({});
    setPendingFileReferenceDeletes([]);
    setLocalPlanItems([]);
    setLocallyCompletedPlanIds([]);
    setNewBillErrors({});
    setNewRecordErrors({});
    setBillFieldErrors({});
    resetNewRecordForm();
    resetNewBillForm();
  };

  const clearNewBillFieldError = (field: InlineTreatmentBillFieldErrorKey) => {
    setNewBillErrors((prev) => (prev[field] ? omit(prev, field) : prev));
  };

  const clearNewRecordFieldError = (
    field: InlineTreatmentRecordFieldErrorKey,
  ) => {
    setNewRecordErrors((prev) => (prev[field] ? omit(prev, field) : prev));
  };

  const clearBillFieldError = (
    billId: string,
    field: InlineTreatmentBillFieldErrorKey,
  ) => {
    setBillFieldErrors((prev) => {
      if (!(field in (prev[billId] ?? {}))) {
        return prev;
      }

      const billErrors = prev[billId] ?? {};
      const nextBillErrors = omit(billErrors, field);
      if (Object.keys(nextBillErrors).length === 0) {
        return omit(prev, billId);
      }

      return {
        ...prev,
        [billId]: nextBillErrors,
      };
    });
  };

  const resetNewRecordForm = () => {
    setShowNewRecordForm(false);
    setNewRecordTempId(null);
    setNewRecordErrors({});
    setNewRecord({
      name: "",
      status: "Requested",
      requestDate: today,
      receivedDate: "",
      rejectedDate: "",
      rejectedReason: "",
    });
  };

  const resetNewBillForm = () => {
    setShowNewBillForm(false);
    setNewBillTempId(null);
    setNewBillErrors({});
    setNewBill({
      recordLabel: "",
      recordStatus: "Requested",
      recordRequestDate: today,
      recordReceivedDate: "",
      recordRejectedDate: "",
      originalBill: "",
      goalAmount: "",
      adjustedBill: "",
      negotiatedAmount: "",
      clientPaid: "",
      insurancePaid: "",
      medpayPipPaid: "",
      lien: "",
    });
  };

  const onSubmit = async (data: z.infer<typeof appointmentFormSchema>) => {
    const submittedBillIds = data.medicalTreatmentBillId ?? [];
    const submittedRecordIds = data.medicalTreatmentRecordId ?? [];
    const currentRecordIds =
      appointmentWithConnections?.records.map((record) => record.id) ?? [];
    const currentBillIds =
      appointmentWithConnections?.bills.map((bill) => bill.id) ?? [];
    const needsConnectionCreate =
      showNewRecordForm ||
      showNewBillForm ||
      localPlanItems.length > 0 ||
      difference(submittedRecordIds, currentRecordIds).length > 0 ||
      difference(submittedBillIds, currentBillIds).length > 0;
    const needsConnectionDelete =
      difference(currentRecordIds, submittedRecordIds).length > 0 ||
      difference(currentBillIds, submittedBillIds).length > 0;
    if (
      (needsConnectionCreate && !canCreateAppointmentConnections) ||
      (needsConnectionDelete && !canDeleteAppointmentConnections)
    ) {
      toast.error(
        "You do not have permission to change treatment date associations.",
      );
      return;
    }
    const attachedBillsForValidation = selectedTreatment
      ? matter.medicalTreatmentBills.filter(
          (bill) =>
            bill.medicalTreatmentId === selectedTreatment.id &&
            submittedBillIds.includes(bill.id),
        )
      : [];
    const nextBillFieldErrors: Record<string, InlineTreatmentBillFieldErrors> =
      {};
    let firstBillErrorId: string | null = null;
    let nextNewBillErrors: InlineTreatmentBillFieldErrors = {};
    let nextNewRecordErrors: InlineTreatmentRecordFieldErrors = {};
    let shouldOpenNewBillForm = false;
    let shouldOpenNewRecordForm = false;

    if (showNewRecordForm) {
      nextNewRecordErrors = validateInlineTreatmentRecordForm(newRecord);
      if (Object.keys(nextNewRecordErrors).length > 0) {
        shouldOpenNewRecordForm = true;
      }
    }

    if (showNewBillForm) {
      nextNewBillErrors = validateInlineTreatmentBillForm(newBill);
      if (Object.keys(nextNewBillErrors).length > 0) {
        shouldOpenNewBillForm = true;
      }
    }

    for (const bill of attachedBillsForValidation) {
      const draft = billDrafts[bill.id] ?? {};
      if (Object.keys(draft).length === 0) {
        continue;
      }

      const errors = validateInlineTreatmentBillForm({ ...bill, ...draft });
      if (Object.keys(errors).length > 0) {
        nextBillFieldErrors[bill.id] = errors;
        firstBillErrorId ??= bill.id;
      }
    }

    if (
      Object.keys(nextBillFieldErrors).length > 0 ||
      Object.keys(nextNewBillErrors).length > 0 ||
      Object.keys(nextNewRecordErrors).length > 0
    ) {
      setBillFieldErrors(nextBillFieldErrors);
      setNewBillErrors(nextNewBillErrors);
      setNewRecordErrors(nextNewRecordErrors);
      if (Object.keys(nextNewRecordErrors).length > 0) {
        setRightPanel("records");
        if (shouldOpenNewRecordForm) {
          setShowNewRecordForm(true);
        }
      } else {
        setRightPanel("bills");
        if (shouldOpenNewBillForm) {
          setShowNewBillForm(true);
        }
        if (firstBillErrorId) {
          setExpandedBillId(firstBillErrorId);
        }
      }
      return;
    }

    setBillFieldErrors({});
    setNewBillErrors({});
    setNewRecordErrors({});
    setIsSubmittingWorkflow(true);
    try {
      let resData;
      const contactConnectionPromises: Promise<unknown>[] = [];

      const {
        medicalTreatmentRecordId: submittedRecordIds,
        medicalTreatmentBillId: submittedBillIdsFromForm,
        ...rest
      } = data;
      let existingSubmittedRecordIds = submittedRecordIds ?? [];
      let existingSubmittedBillIds = submittedBillIdsFromForm ?? [];

      if (showNewRecordForm) {
        const createdRecordId = await createNewRecordFromForm({
          recordData: newRecord,
          stagingItemId: newRecordTempId,
        });
        existingSubmittedRecordIds = Array.from(
          new Set([...existingSubmittedRecordIds, createdRecordId]),
        );
      }

      if (showNewBillForm) {
        try {
          const createdBillId = await createNewBillFromForm({
            billData: newBill,
            stagingItemId: newBillTempId,
          });
          existingSubmittedBillIds = Array.from(
            new Set([...existingSubmittedBillIds, createdBillId]),
          );
        } catch (error) {
          if (error instanceof InlineTreatmentBillValidationError) {
            setNewBillErrors(error.errors);
            setShowNewBillForm(true);
            setRightPanel("bills");
            setIsSubmittingWorkflow(false);
            return;
          }
          throw error;
        }
      }

      const existingRecordIds =
        appointmentWithConnections?.records.map((r) => r.id) ?? [];
      const existingBillIds =
        appointmentWithConnections?.bills.map((b) => b.id) ?? [];

      if (selectedTreatment && editing && selectedAppointment) {
        try {
          const result = await updateAppointment.mutateAsync({
            ...rest,
            outcome: rest.outcome || null,
            missedReason:
              rest.outcome === "missed" ? rest.missedReason || null : null,
            matterId: matter.id,
            id: selectedAppointment.id,
            dateModified: selectedAppointment.dateModified,
            dateEnd: rest.multiday && rest.dateEnd ? rest.dateEnd : null,
          });
          resData = result;
          if (!recordsNotApplicable && !rest.recordNotApplicable) {
            contactConnectionPromises.push(
              updateAppointmentConnections.mutateAsync({
                matter,
                existingItems: existingRecordIds,
                newItems: existingSubmittedRecordIds,
                appointmentId: selectedAppointment.id,
                itemType: "record",
              }),
            );
          }
          if (!billsNotApplicable && !rest.billNotApplicable) {
            contactConnectionPromises.push(
              updateAppointmentConnections.mutateAsync({
                matter,
                existingItems: existingBillIds,
                newItems: existingSubmittedBillIds,
                appointmentId: selectedAppointment.id,
                itemType: "bill",
              }),
            );
          }
          Object.entries(billDrafts).forEach(([billId, draft]) => {
            if (!draft) return;
            const billEntity = matter.medicalTreatmentBills.find(
              (b) => b.id === billId,
            );
            if (billEntity) {
              const recordStatus =
                draft.recordStatus ?? billEntity.recordStatus;
              contactConnectionPromises.push(
                updateBill.mutateAsync({
                  matterId: matter.id,
                  id: billId,
                  dateModified: billEntity.dateModified,
                  ...draft,
                  ...(canSetMedicalTreatmentBillLien(recordStatus)
                    ? {}
                    : { lien: "" }),
                }),
              );
            }
          });
          // Flush record drafts
          Object.entries(recordDrafts).forEach(([recordId, draft]) => {
            const recordEntity = matter.medicalTreatmentRecords.find(
              (r) => r.id === recordId,
            );
            if (recordEntity) {
              contactConnectionPromises.push(
                updateRecord.mutateAsync({
                  matterId: matter.id,
                  id: recordId,
                  dateModified: recordEntity.dateModified,
                  ...draft,
                }),
              );
            }
          });
          Object.entries(existingBillFiles).forEach(([billId, files]) => {
            if (
              files.length === 0 ||
              billId.startsWith(PENDING_FILE_STAGING_PREFIX)
            ) {
              return;
            }
            contactConnectionPromises.push(
              createExistingItemFileReferences({
                files,
                itemId: billId,
                itemType: "bill",
              }),
            );
          });
          Object.entries(existingRecordFiles).forEach(([recordId, files]) => {
            if (
              files.length === 0 ||
              recordId.startsWith(PENDING_FILE_STAGING_PREFIX)
            ) {
              return;
            }
            contactConnectionPromises.push(
              createExistingItemFileReferences({
                files,
                itemId: recordId,
                itemType: "record",
              }),
            );
          });
          pendingFileReferenceDeletes.forEach((id) => {
            contactConnectionPromises.push(
              deleteMatterFileReference.mutateAsync({
                matterId: matter.id,
                id,
              }),
            );
          });
          await Promise.all(contactConnectionPromises);
          for (const item of localPlanItems) {
            const createdPlan = await createPlan.mutateAsync({
              matterId: matter.id,
              date: rest.date,
              status: "Not Started",
              description: item.description,
            });
            await updateAppointmentConnections.mutateAsync({
              matter,
              existingItems: [],
              newItems: [createdPlan.data.id],
              appointmentId: selectedAppointment.id,
              itemType: "plan",
            });
          }
          for (const planId of locallyCompletedPlanIds) {
            const planEntity = matter.medicalTreatmentPlans.find(
              (plan) => plan.id === planId,
            );
            if (!planEntity) continue;
            await updatePlan.mutateAsync({
              matterId: matter.id,
              id: planEntity.id,
              dateModified: planEntity.dateModified,
              date: planEntity.date,
              description: planEntity.description,
              status: "Completed",
            });
            await syncPlanFulfillerConnection(planEntity.id);
          }
          resetLocalWorkflowState();
          setOpen(false);
          toast.success("Treatment date updated successfully");
          void invalidateMatterAndSearchQueries(queryClient);
        } catch (error) {
          console.error(error);
        }
      } else if (selectedTreatment) {
        try {
          const result = await createAppointment.mutateAsync({
            ...rest,
            outcome: rest.outcome || null,
            missedReason:
              rest.outcome === "missed" ? rest.missedReason || null : null,
            matterId: matter.id,
            medicalTreatmentId: selectedTreatment.id,
          });
          resData = result;
          if (!billsNotApplicable && !rest.billNotApplicable) {
            contactConnectionPromises.push(
              updateAppointmentConnections.mutateAsync({
                matter,
                existingItems: existingBillIds,
                newItems: existingSubmittedBillIds,
                appointmentId: result.data.id,
                itemType: "bill",
              }),
            );
          }
          if (!recordsNotApplicable && !rest.recordNotApplicable) {
            contactConnectionPromises.push(
              updateAppointmentConnections.mutateAsync({
                matter,
                existingItems: existingRecordIds,
                newItems: existingSubmittedRecordIds,
                appointmentId: result.data.id,
                itemType: "record",
              }),
            );
          }
          Object.entries(billDrafts).forEach(([billId, draft]) => {
            if (!draft) return;
            const billEntity = matter.medicalTreatmentBills.find(
              (b) => b.id === billId,
            );
            if (billEntity) {
              const recordStatus =
                draft.recordStatus ?? billEntity.recordStatus;
              contactConnectionPromises.push(
                updateBill.mutateAsync({
                  matterId: matter.id,
                  id: billId,
                  dateModified: billEntity.dateModified,
                  ...draft,
                  ...(canSetMedicalTreatmentBillLien(recordStatus)
                    ? {}
                    : { lien: "" }),
                }),
              );
            }
          });
          // Flush record drafts
          Object.entries(recordDrafts).forEach(([recordId, draft]) => {
            const recordEntity = matter.medicalTreatmentRecords.find(
              (r) => r.id === recordId,
            );
            if (recordEntity) {
              contactConnectionPromises.push(
                updateRecord.mutateAsync({
                  matterId: matter.id,
                  id: recordId,
                  dateModified: recordEntity.dateModified,
                  ...draft,
                }),
              );
            }
          });
          Object.entries(existingBillFiles).forEach(([billId, files]) => {
            if (
              files.length === 0 ||
              billId.startsWith(PENDING_FILE_STAGING_PREFIX)
            ) {
              return;
            }
            contactConnectionPromises.push(
              createExistingItemFileReferences({
                files,
                itemId: billId,
                itemType: "bill",
              }),
            );
          });
          Object.entries(existingRecordFiles).forEach(([recordId, files]) => {
            if (
              files.length === 0 ||
              recordId.startsWith(PENDING_FILE_STAGING_PREFIX)
            ) {
              return;
            }
            contactConnectionPromises.push(
              createExistingItemFileReferences({
                files,
                itemId: recordId,
                itemType: "record",
              }),
            );
          });
          pendingFileReferenceDeletes.forEach((id) => {
            contactConnectionPromises.push(
              deleteMatterFileReference.mutateAsync({
                matterId: matter.id,
                id,
              }),
            );
          });
          await Promise.all(contactConnectionPromises);
          for (const item of localPlanItems) {
            const createdPlan = await createPlan.mutateAsync({
              matterId: matter.id,
              date: rest.date,
              status: "Not Started",
              description: item.description,
            });
            await updateAppointmentConnections.mutateAsync({
              matter,
              existingItems: [],
              newItems: [createdPlan.data.id],
              appointmentId: result.data.id,
              itemType: "plan",
            });
          }
          for (const planId of locallyCompletedPlanIds) {
            const planEntity = matter.medicalTreatmentPlans.find(
              (plan) => plan.id === planId,
            );
            if (!planEntity) continue;
            await updatePlan.mutateAsync({
              matterId: matter.id,
              id: planEntity.id,
              dateModified: planEntity.dateModified,
              date: planEntity.date,
              description: planEntity.description,
              status: "Completed",
            });
            await syncPlanFulfillerConnection(planEntity.id);
          }
          resetLocalWorkflowState();
          updateSlideoverPayload("treatment-appointment", {
            title: SLIDEOVER_TITLE.TREATMENT_APPOINTMENT.UPDATE,
            description: SLIDEOVER_DESCRIPTION.TREATMENT_APPOINTMENT.UPDATE,
            editing: true,
            appointmentId: resData.data.id,
            treatmentId: selectedTreatment.id,
          });
          setOpen(false);
          toast.success("Treatment date created successfully");
          void invalidateMatterAndSearchQueries(queryClient);
        } catch (error) {
          console.error(error);
        }
      }

      if (resData) {
        const sanitizedData = omitBy(
          resData.data,
          isNil,
        ) as MedicalTreatmentAppointment;
        appointmentForm.reset({
          ...defaultFormValues,
          ...sanitizedData,
          dateEnd: sanitizedData.dateEnd ?? null,
          multiday: sanitizedData.multiday ?? false,
          billNotApplicable: sanitizedData.billNotApplicable ?? false,
          recordNotApplicable: sanitizedData.recordNotApplicable ?? false,
          medicalTreatmentRecordId:
            appointmentWithConnections?.records.map((r) => r.id) ?? [],
          medicalTreatmentBillId:
            appointmentWithConnections?.bills.map((b) => b.id) ?? [],
        });
      }
    } finally {
      setIsSubmittingWorkflow(false);
    }
  };

  useEffect(() => {
    if (!open) {
      const resetRequestDate = dayjs().format("YYYY-MM-DD");
      setConfirmClose(false);
      appointmentForm.reset(defaultFormValues);
      setShowAttachExistingRecords(false);
      setShowAttachExistingBills(false);
      setExpandedBillId(null);
      setExpandedRecordId(null);
      setBillDrafts({});
      setRecordDrafts({});
      setExistingRecordFiles({});
      setExistingBillFiles({});
      setActiveFileReferenceTarget(null);
      setPendingFileReferenceDeletes([]);
      setShowNewRecordForm(false);
      setNewRecordTempId(null);
      setLocalPlanItems([]);
      setLocallyCompletedPlanIds([]);
      setNewRecord({
        name: "",
        status: "Requested",
        requestDate: resetRequestDate,
        receivedDate: "",
        rejectedDate: "",
        rejectedReason: "",
      });
      setShowNewBillForm(false);
      setNewBillTempId(null);
      setNewBill({
        recordLabel: "",
        recordStatus: "Requested",
        recordRequestDate: resetRequestDate,
        recordReceivedDate: "",
        originalBill: "",
        goalAmount: "",
        adjustedBill: "",
        negotiatedAmount: "",
        clientPaid: "",
        insurancePaid: "",
        medpayPipPaid: "",
        lien: "",
        recordRejectedDate: "",
      });
      setNewBillErrors({});
      setNewRecordErrors({});
      setBillFieldErrors({});
    } else {
      setRightPanel("records");
    }
  }, [open, appointmentForm]);

  const isMultiday = appointmentForm.watch("multiday");
  const watchRecordIds =
    appointmentForm.watch("medicalTreatmentRecordId") ?? [];
  const watchBillIds = appointmentForm.watch("medicalTreatmentBillId") ?? [];
  const watchRecordNotApplicable = appointmentForm.watch("recordNotApplicable");
  const watchBillNotApplicable = appointmentForm.watch("billNotApplicable");

  const allRecordsForTreatment = selectedTreatment
    ? matter.medicalTreatmentRecords.filter(
        (r) => r.medicalTreatmentId === selectedTreatment.id,
      )
    : [];
  const attachedRecords = allRecordsForTreatment.filter((r) =>
    watchRecordIds.includes(r.id),
  );
  const availableRecords = allRecordsForTreatment.filter(
    (r) => !watchRecordIds.includes(r.id),
  );

  const allBillsForTreatment = selectedTreatment
    ? matter.medicalTreatmentBills.filter(
        (b) => b.medicalTreatmentId === selectedTreatment.id,
      )
    : [];
  const attachedBills = allBillsForTreatment.filter((b) =>
    watchBillIds.includes(b.id),
  );
  const availableBills = allBillsForTreatment.filter(
    (b) => !watchBillIds.includes(b.id),
  );

  const clearStagedItemFiles = (
    itemId: string,
    itemType: "bill" | "record",
  ) => {
    const setItemFiles =
      itemType === "bill" ? setExistingBillFiles : setExistingRecordFiles;
    setItemFiles((prev) => {
      if (!(itemId in prev)) {
        return prev;
      }
      const { [itemId]: removed, ...rest } = prev;
      void removed;
      return rest;
    });
  };

  const createExistingItemFileReferences = async ({
    files,
    itemId,
    itemType,
  }: {
    files: MatterFile[];
    itemId: string;
    itemType: "bill" | "record";
  }) => {
    if (files.length === 0) return;

    await Promise.all(
      files.map((file) =>
        createMatterFileReference.mutateAsync({
          matterId: matter.id,
          fileId: file.id,
          medicalTreatmentBillId: itemType === "bill" ? itemId : undefined,
          medicalTreatmentRecordId: itemType === "record" ? itemId : undefined,
        }),
      ),
    );
  };

  const stageExistingItemFileReferences = (
    itemId: string,
    itemType: "bill" | "record",
    selectedFiles: MatterFile[],
  ) => {
    const fileReferenceKey =
      itemType === "bill"
        ? "medicalTreatmentBillId"
        : "medicalTreatmentRecordId";
    const existingReferences = mergeFilesAndReferenceId(
      matter,
      fileReferenceKey,
      itemId,
    );
    const referenceByFileId = new Map(
      existingReferences.map((reference) => [
        reference.id,
        reference.fileReferenceId,
      ]),
    );

    const nextFiles = selectedFiles.filter((file) => {
      const existingReferenceId = referenceByFileId.get(file.id);
      if (!existingReferenceId) {
        return true;
      }

      setPendingFileReferenceDeletes((prev) =>
        prev.filter((id) => id !== existingReferenceId),
      );
      return false;
    });

    const setItemFiles =
      itemType === "bill" ? setExistingBillFiles : setExistingRecordFiles;
    setItemFiles((prev) => {
      const mergedFiles = [...(prev[itemId] ?? []), ...nextFiles];
      const uniqueFiles = mergedFiles.filter(
        (file, index, allFiles) =>
          allFiles.findIndex((candidate) => candidate.id === file.id) === index,
      );

      return {
        ...prev,
        [itemId]: uniqueFiles,
      };
    });
    setActiveFileReferenceTarget(null);
  };

  const createNewRecordFromForm = async ({
    recordData,
    stagingItemId,
  }: {
    recordData: NewRecordFormValues;
    stagingItemId?: string | null;
  }) => {
    if (!selectedTreatment) {
      throw new Error("Treatment is required to create a record");
    }
    const result = await createRecord.mutateAsync({
      matterId: matter.id,
      medicalTreatmentId: selectedTreatment.id,
      name: recordData.name.trim(),
      status: recordData.status,
      requestDate: ["Requested", "Received", "Rejected"].includes(
        recordData.status,
      )
        ? recordData.requestDate || undefined
        : undefined,
      receivedDate:
        recordData.status === "Received"
          ? recordData.receivedDate || undefined
          : undefined,
      rejectedDate:
        recordData.status === "Rejected"
          ? recordData.rejectedDate || undefined
          : undefined,
      rejectedReason:
        recordData.status === "Rejected"
          ? recordData.rejectedReason || undefined
          : undefined,
    });

    if (recordData.status === "Received") {
      const stagedFiles = stagingItemId
        ? (existingRecordFiles[stagingItemId] ?? [])
        : [];
      if (stagedFiles.length > 0) {
        await createExistingItemFileReferences({
          files: stagedFiles,
          itemId: result.data.id,
          itemType: "record",
        });
        if (stagingItemId) {
          setExistingRecordFiles((prev) => omit(prev, stagingItemId));
        }
      }
    }

    return result.data.id;
  };

  const createNewBillFromForm = async ({
    billData,
    stagingItemId,
  }: {
    billData: NewBillFormValues;
    stagingItemId?: string | null;
  }) => {
    if (!selectedTreatment) {
      throw new Error("Treatment is required to create a bill");
    }
    assertInlineTreatmentBillFormValid(billData);
    const isReceivedBill = billData.recordStatus === "Received";
    const result = await createBill.mutateAsync({
      matterId: matter.id,
      medicalTreatmentId: selectedTreatment.id,
      recordLabel: billData.recordLabel.trim(),
      recordStatus: billData.recordStatus,
      recordRequestDate: billData.recordRequestDate || undefined,
      recordReceivedDate: billData.recordReceivedDate || undefined,
      recordRejectedDate:
        billData.recordStatus === "Rejected"
          ? billData.recordRejectedDate || undefined
          : undefined,
      originalBill:
        isReceivedBill && billData.originalBill
          ? Number(billData.originalBill)
          : null,
      goalAmount:
        isReceivedBill && billData.goalAmount
          ? Number(billData.goalAmount)
          : null,
      adjustedBill:
        isReceivedBill && billData.adjustedBill
          ? Number(billData.adjustedBill)
          : null,
      negotiatedAmount:
        isReceivedBill && billData.negotiatedAmount
          ? Number(billData.negotiatedAmount)
          : null,
      clientPaid:
        isReceivedBill && billData.clientPaid
          ? Number(billData.clientPaid)
          : null,
      insurancePaid:
        isReceivedBill && billData.insurancePaid
          ? Number(billData.insurancePaid)
          : null,
      medpayPipPaid:
        isReceivedBill && billData.medpayPipPaid
          ? Number(billData.medpayPipPaid)
          : null,
      ...(canSetMedicalTreatmentBillLien(billData.recordStatus) && billData.lien
        ? {
            lien: billData.lien as (typeof MEDICAL_TREATMENT_LIEN_STATUS_OPTIONS)[number],
          }
        : {}),
    });

    if (isReceivedBill) {
      const stagedFiles = stagingItemId
        ? (existingBillFiles[stagingItemId] ?? [])
        : [];
      if (stagedFiles.length > 0) {
        await createExistingItemFileReferences({
          files: stagedFiles,
          itemId: result.data.id,
          itemType: "bill",
        });
        if (stagingItemId) {
          setExistingBillFiles((prev) => omit(prev, stagingItemId));
        }
      }
    }

    return result.data.id;
  };

  const ExistingItemFileReferences = ({
    itemId,
    itemType,
  }: {
    itemId: string;
    itemType: "bill" | "record";
  }) => {
    const itemFiles =
      itemType === "bill"
        ? (existingBillFiles[itemId] ?? [])
        : (existingRecordFiles[itemId] ?? []);
    const setItemFiles =
      itemType === "bill" ? setExistingBillFiles : setExistingRecordFiles;
    const fileReferences = mergeFilesAndReferenceId(
      matter,
      itemType === "bill"
        ? "medicalTreatmentBillId"
        : "medicalTreatmentRecordId",
      itemId,
    ).filter(
      (fileReference) =>
        !pendingFileReferenceDeletes.includes(fileReference.fileReferenceId),
    );

    return (
      <div className="space-y-2">
        <InlineFieldLabel>Attached files</InlineFieldLabel>
        {fileReferences.length > 0 && (
          <div className="space-y-1">
            {fileReferences.map((fileReference) => (
              <div
                key={fileReference.fileReferenceId}
                className="border-border flex items-center gap-2 rounded-md border px-2 py-1 text-sm"
              >
                <FileText className="text-muted-foreground h-4 w-4 shrink-0" />
                <span className="min-w-0 flex-1 truncate">
                  {fileReference.name}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => {
                    setPendingFileReferenceDeletes((prev) =>
                      prev.includes(fileReference.fileReferenceId)
                        ? prev
                        : [...prev, fileReference.fileReferenceId],
                    );
                  }}
                >
                  <XIcon className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
        {fileReferences.length === 0 && (
          <p className="text-muted-foreground text-xs">
            No files attached yet.
          </p>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => {
            setActiveFileReferenceTarget({
              itemId,
              itemType,
            });
          }}
        >
          Select from file repo
        </Button>
        {itemFiles.length > 0 && (
          <div className="space-y-1">
            {itemFiles.map((file) => (
              <div
                key={file.id}
                className="border-border flex items-center gap-2 rounded-md border px-2 py-1 text-sm"
              >
                <FileText className="text-muted-foreground h-4 w-4 shrink-0" />
                <span className="min-w-0 flex-1 truncate">{file.name}</span>
                <span className="text-muted-foreground text-xs">
                  Pending save
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => {
                    setItemFiles((prev) => ({
                      ...prev,
                      [itemId]: (prev[itemId] ?? []).filter(
                        (candidate) => candidate.id !== file.id,
                      ),
                    }));
                  }}
                >
                  <XIcon className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(v) => {
          if (!v) {
            if (hasUnsavedChanges) {
              setConfirmClose(true);
              return;
            }
            setOpen(false);
          }
        }}
      >
        <DialogContent className="flex h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-6xl flex-col gap-0 overflow-hidden p-0 sm:h-[90vh] sm:max-h-[760px]">
          <DialogHeader className="border-border min-w-0 shrink-0 border-b px-5 py-3 pr-12 text-left sm:text-left">
            <DialogTitle
              className="line-clamp-4 min-w-0 text-lg font-semibold tracking-tight break-words"
              title={title}
            >
              {title}
            </DialogTitle>
            <DialogDescription className="sr-only">{title}</DialogDescription>
          </DialogHeader>

          <Form {...appointmentForm}>
            <form
              className="flex min-h-0 flex-1 flex-col"
              onSubmit={appointmentForm.handleSubmit(onSubmit)}
            >
              <div className="relative flex min-h-0 flex-1">
                {isFormLoading && <FormLoadingOverlay />}

                {/* Left panel – Treatment Details */}
                <div className="border-border flex min-h-0 min-w-0 flex-1 flex-col border-r">
                  <div className="border-border flex-shrink-0 border-b px-4 py-3">
                    <h3 className="flex items-center gap-2 text-sm font-semibold">
                      <Calendar className="h-4 w-4" />
                      Treatment details
                    </h3>
                  </div>
                  <div className="flex-1 space-y-4 overflow-y-auto p-4">
                    <DateFormField
                      register={appointmentForm.register("date")}
                      label="Date*"
                      name="date"
                    />

                    <TimeFormField
                      register={appointmentForm.register("time")}
                      label="Time"
                      name="time"
                    />

                    <div className="flex items-center gap-3">
                      <SwitchFormField
                        register={appointmentForm.register("multiday")}
                        label={<Typography size="sm">Date range</Typography>}
                      />
                    </div>

                    {isMultiday && (
                      <DateFormField
                        register={appointmentForm.register("dateEnd")}
                        label="End date"
                        name="dateEnd"
                      />
                    )}

                    <TreatmentAppointmentOutcomeFields
                      legacyReason={appointmentWithConnections?.noShowReason}
                    />

                    <div className="max-w-[250px]">
                      <MultiselectFormField
                        options={CONTACT_ORGANIZATION_MEDICAL_PROVIDER_SPECIALIZATIONS.map(
                          (specialization) => ({
                            label: specialization,
                            value: specialization,
                          }),
                        )}
                        label="Treatment type*"
                        register={appointmentForm.register("type")}
                        maxCount={1}
                      />
                    </div>

                    {!completeTreatment?.planNotApplicable &&
                      selectedTreatment && (
                        <MatterTreatmentPlansCard
                          plans={matter.medicalTreatmentPlans}
                          matter={matter}
                          treatmentId={selectedTreatment.id}
                          localPlanItems={localPlanItems}
                          onAddLocalPlanItem={(description) => {
                            setLocalPlanItems((prev) => [
                              ...prev,
                              {
                                id: `local-plan-${Date.now()}-${prev.length}`,
                                description,
                              },
                            ]);
                          }}
                          onRemoveLocalPlanItem={(itemId) => {
                            setLocalPlanItems((prev) =>
                              prev.filter((item) => item.id !== itemId),
                            );
                          }}
                          locallyCompletedPlanIds={locallyCompletedPlanIds}
                          onToggleLocalPlanCompleted={(planId) => {
                            setLocallyCompletedPlanIds((prev) =>
                              prev.includes(planId)
                                ? prev.filter((id) => id !== planId)
                                : [...prev, planId],
                            );
                          }}
                        />
                      )}

                    <TextAreaFormField
                      register={appointmentForm.register("description")}
                      label="Notes"
                    />
                  </div>
                </div>

                {/* Right panel – Records / Bills */}
                <div className="bg-muted/30 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                  {/* Segmented control + Not applicable toggle */}
                  <div className="border-border bg-muted/30 flex flex-shrink-0 items-center gap-2 border-b px-4 py-3">
                    <div className="bg-muted flex items-center gap-0.5 rounded-lg p-1">
                      {(["records", "bills"] as const).map((panel) => (
                        <button
                          key={panel}
                          type="button"
                          onClick={() => {
                            setRightPanel(panel);
                          }}
                          className={cn(
                            "rounded-md px-4 py-1.5 text-sm font-medium transition-all",
                            rightPanel === panel
                              ? "bg-background text-foreground shadow-sm"
                              : "text-muted-foreground hover:text-foreground",
                          )}
                        >
                          {panel === "records" ? "Medical Records" : "Bills"}
                        </button>
                      ))}
                    </div>
                    {selectedTreatment && (
                      <label className="text-muted-foreground ml-auto flex cursor-pointer items-center gap-1.5 text-xs select-none">
                        <Checkbox
                          checked={
                            rightPanel === "records"
                              ? watchRecordNotApplicable
                              : watchBillNotApplicable
                          }
                          disabled={
                            rightPanel === "records"
                              ? recordsNotApplicable ||
                                watchRecordIds.length > 0
                              : billsNotApplicable || watchBillIds.length > 0
                          }
                          onCheckedChange={(checked) => {
                            if (rightPanel === "records") {
                              appointmentForm.setValue(
                                "recordNotApplicable",
                                Boolean(checked),
                                { shouldDirty: true },
                              );
                            } else {
                              appointmentForm.setValue(
                                "billNotApplicable",
                                Boolean(checked),
                                { shouldDirty: true },
                              );
                            }
                          }}
                        />
                        Not applicable
                      </label>
                    )}
                  </div>

                  <div className="flex-1 space-y-4 overflow-y-auto p-4">
                    {!selectedTreatment ? (
                      <p className="text-muted-foreground text-sm">
                        No treatment selected.
                      </p>
                    ) : rightPanel === "records" ? (
                      recordsNotApplicable || watchRecordNotApplicable ? (
                        <p className="text-muted-foreground text-sm">
                          Medical records are marked as not applicable for this{" "}
                          {recordsNotApplicable ? "treatment" : "appointment"}.
                        </p>
                      ) : showNewRecordForm ? (
                        <div className="space-y-4">
                          {attachedRecords.length > 0 && (
                            <>
                              <div className="space-y-1.5">
                                {attachedRecords.map((record) => (
                                  <div
                                    key={record.id}
                                    className="border-border bg-background flex items-center gap-2 rounded-md border px-3 py-2"
                                  >
                                    <FileText className="text-muted-foreground h-4 w-4 shrink-0" />
                                    <div className="min-w-0 flex-1">
                                      <p className="truncate text-sm font-medium">
                                        {trimText(record.name) || "-"}
                                      </p>
                                      <p className="text-muted-foreground text-xs">
                                        {record.status === "Received"
                                          ? `Received${record.receivedDate ? ` ${formatDate(record.receivedDate)}` : ""}`
                                          : record.status === "Rejected"
                                            ? `Rejected${record.rejectedDate ? ` ${formatDate(record.rejectedDate)}` : ""}`
                                            : `Requested${record.requestDate ? ` ${formatDate(record.requestDate)}` : ""}`}
                                      </p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                              <Separator />
                            </>
                          )}
                          <p className="text-sm font-semibold">
                            Upload new record
                          </p>

                          {newRecord.status === "Received" && (
                            <InlineTextField
                              label="Record name"
                              value={newRecord.name}
                              error={newRecordErrors.name}
                              onChange={(value) => {
                                clearNewRecordFieldError("name");
                                setNewRecord((prev) => ({
                                  ...prev,
                                  name: value,
                                }));
                              }}
                            />
                          )}

                          <InlineSelectField
                            label="Status"
                            options={
                              MEDICAL_TREATMENT_RECORD_SELECTABLE_STATUS_OPTIONS
                            }
                            value={newRecord.status}
                            error={newRecordErrors.status}
                            onChange={(value) => {
                              clearNewRecordFieldError("status");
                              clearNewRecordFieldError("requestDate");
                              clearNewRecordFieldError("receivedDate");
                              clearNewRecordFieldError("rejectedDate");
                              setNewRecord((prev) => ({
                                ...prev,
                                status: value,
                                requestDate:
                                  [
                                    "Requested",
                                    "Received",
                                    "Rejected",
                                  ].includes(value) && !prev.requestDate
                                    ? today
                                    : prev.requestDate,
                                receivedDate:
                                  value === "Received" && !prev.receivedDate
                                    ? today
                                    : prev.receivedDate,
                                rejectedDate:
                                  value === "Rejected" && !prev.rejectedDate
                                    ? today
                                    : prev.rejectedDate,
                              }));
                              if (value !== "Received" && newRecordTempId) {
                                clearStagedItemFiles(newRecordTempId, "record");
                              }
                            }}
                          />

                          {["Requested", "Received", "Rejected"].includes(
                            newRecord.status,
                          ) && (
                            <InlineDateField
                              label="Request date"
                              required
                              value={newRecord.requestDate}
                              error={newRecordErrors.requestDate}
                              onChange={(value) => {
                                clearNewRecordFieldError("requestDate");
                                setNewRecord((prev) => ({
                                  ...prev,
                                  requestDate: value,
                                }));
                              }}
                            />
                          )}

                          {newRecord.status === "Received" && (
                            <InlineDateField
                              label="Received date"
                              required
                              value={newRecord.receivedDate}
                              error={newRecordErrors.receivedDate}
                              onChange={(value) => {
                                clearNewRecordFieldError("receivedDate");
                                setNewRecord((prev) => ({
                                  ...prev,
                                  receivedDate: value,
                                }));
                              }}
                            />
                          )}

                          {newRecord.status === "Rejected" && (
                            <>
                              <InlineDateField
                                label="Rejected date"
                                required
                                value={newRecord.rejectedDate}
                                error={newRecordErrors.rejectedDate}
                                onChange={(value) => {
                                  clearNewRecordFieldError("rejectedDate");
                                  setNewRecord((prev) => ({
                                    ...prev,
                                    rejectedDate: value,
                                  }));
                                }}
                              />
                              <InlineTextareaField
                                label="Rejected reason"
                                value={newRecord.rejectedReason}
                                onChange={(value) => {
                                  setNewRecord((prev) => ({
                                    ...prev,
                                    rejectedReason: value,
                                  }));
                                }}
                              />
                            </>
                          )}

                          {newRecord.status === "Received" &&
                            newRecordTempId && (
                              <ExistingItemFileReferences
                                itemId={newRecordTempId}
                                itemType="record"
                              />
                            )}

                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                if (newRecordTempId) {
                                  clearStagedItemFiles(
                                    newRecordTempId,
                                    "record",
                                  );
                                }
                                resetNewRecordForm();
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {attachedRecords.length > 0 && (
                            <div className="space-y-1.5">
                              {attachedRecords.map((record) => {
                                const recordDraft =
                                  recordDrafts[record.id] ?? {};
                                const isExpanded =
                                  expandedRecordId === record.id;
                                const effectiveRecordStatus =
                                  recordDraft.status ?? record.status;
                                return (
                                  <div
                                    key={record.id}
                                    className="border-border bg-background overflow-hidden rounded-md border"
                                  >
                                    <button
                                      type="button"
                                      className="hover:bg-muted/30 flex w-full items-center gap-2 px-3 py-2 transition-colors"
                                      onClick={() => {
                                        if (isExpanded) {
                                          setExpandedRecordId(null);
                                        } else {
                                          setExpandedRecordId(record.id);
                                        }
                                      }}
                                    >
                                      <FileText className="text-muted-foreground h-4 w-4 shrink-0" />
                                      <div className="min-w-0 flex-1 text-left">
                                        <p className="truncate text-sm font-medium">
                                          {trimText(record.name) || "-"}
                                        </p>
                                        <p className="text-muted-foreground text-xs">
                                          {record.status === "Received"
                                            ? `Received${record.receivedDate ? ` ${formatDate(record.receivedDate)}` : ""}`
                                            : record.status === "Rejected"
                                              ? `Rejected${record.rejectedDate ? ` ${formatDate(record.rejectedDate)}` : ""}`
                                              : `Requested${record.requestDate ? ` ${formatDate(record.requestDate)}` : ""}`}
                                        </p>
                                      </div>
                                      <ChevronDown
                                        className={cn(
                                          "text-muted-foreground h-4 w-4 shrink-0 transition-transform",
                                          isExpanded && "rotate-180",
                                        )}
                                      />
                                    </button>
                                    {isExpanded && (
                                      <div className="border-border space-y-3 border-t px-3 py-3">
                                        {effectiveRecordStatus ===
                                          "Received" && (
                                          <InlineTextField
                                            label="Record name"
                                            value={
                                              recordDraft.name ?? record.name
                                            }
                                            onChange={(value) => {
                                              setRecordDrafts((prev) => ({
                                                ...prev,
                                                [record.id]: {
                                                  ...prev[record.id],
                                                  name: value,
                                                },
                                              }));
                                            }}
                                          />
                                        )}
                                        <InlineSelectField
                                          label="Status"
                                          options={
                                            MEDICAL_TREATMENT_RECORD_SELECTABLE_STATUS_OPTIONS
                                          }
                                          value={
                                            recordDraft.status ?? record.status
                                          }
                                          onChange={(newStatus) => {
                                            setRecordDrafts((prev) => {
                                              const existingDraft =
                                                prev[record.id] ?? {};
                                              return {
                                                ...prev,
                                                [record.id]: {
                                                  ...existingDraft,
                                                  status: newStatus,
                                                  requestDate:
                                                    [
                                                      "Requested",
                                                      "Received",
                                                      "Rejected",
                                                    ].includes(newStatus) &&
                                                    !(
                                                      existingDraft.requestDate ??
                                                      record.requestDate
                                                    )
                                                      ? today
                                                      : existingDraft.requestDate,
                                                  receivedDate:
                                                    newStatus === "Received" &&
                                                    !(
                                                      existingDraft.receivedDate ??
                                                      record.receivedDate
                                                    )
                                                      ? today
                                                      : existingDraft.receivedDate,
                                                  rejectedDate:
                                                    newStatus === "Rejected" &&
                                                    !(
                                                      existingDraft.rejectedDate ??
                                                      record.rejectedDate
                                                    )
                                                      ? today
                                                      : existingDraft.rejectedDate,
                                                },
                                              };
                                            });
                                            if (newStatus !== "Received") {
                                              setExistingRecordFiles(
                                                (prev) => ({
                                                  ...prev,
                                                  [record.id]: [],
                                                }),
                                              );
                                            }
                                          }}
                                        />
                                        {[
                                          "Requested",
                                          "Received",
                                          "Rejected",
                                        ].includes(
                                          recordDraft.status ?? record.status,
                                        ) && (
                                          <InlineDateField
                                            label="Request date"
                                            required
                                            value={
                                              recordDraft.requestDate ??
                                              record.requestDate ??
                                              ""
                                            }
                                            onChange={(value) => {
                                              setRecordDrafts((prev) => ({
                                                ...prev,
                                                [record.id]: {
                                                  ...prev[record.id],
                                                  requestDate: value,
                                                },
                                              }));
                                            }}
                                          />
                                        )}
                                        {(recordDraft.status ??
                                          record.status) === "Received" && (
                                          <InlineDateField
                                            label="Received date"
                                            required
                                            value={
                                              recordDraft.receivedDate ??
                                              record.receivedDate ??
                                              ""
                                            }
                                            onChange={(value) => {
                                              setRecordDrafts((prev) => ({
                                                ...prev,
                                                [record.id]: {
                                                  ...prev[record.id],
                                                  receivedDate: value,
                                                },
                                              }));
                                            }}
                                          />
                                        )}
                                        {(recordDraft.status ??
                                          record.status) === "Rejected" && (
                                          <>
                                            <InlineDateField
                                              label="Rejected date"
                                              value={
                                                recordDraft.rejectedDate ??
                                                record.rejectedDate ??
                                                ""
                                              }
                                              onChange={(value) => {
                                                setRecordDrafts((prev) => ({
                                                  ...prev,
                                                  [record.id]: {
                                                    ...prev[record.id],
                                                    rejectedDate: value,
                                                  },
                                                }));
                                              }}
                                            />
                                            <InlineTextareaField
                                              label="Rejected reason"
                                              value={
                                                recordDraft.rejectedReason ??
                                                record.rejectedReason ??
                                                ""
                                              }
                                              onChange={(value) => {
                                                setRecordDrafts((prev) => ({
                                                  ...prev,
                                                  [record.id]: {
                                                    ...prev[record.id],
                                                    rejectedReason: value,
                                                  },
                                                }));
                                              }}
                                            />
                                          </>
                                        )}
                                        {effectiveRecordStatus ===
                                          "Received" && (
                                          <ExistingItemFileReferences
                                            itemId={record.id}
                                            itemType="record"
                                          />
                                        )}
                                        <div className="border-border border-t pt-3">
                                          <button
                                            type="button"
                                            disabled={
                                              !canDeleteAppointmentConnections
                                            }
                                            className="text-destructive hover:text-destructive/80 text-sm transition-colors"
                                            onClick={() => {
                                              appointmentForm.setValue(
                                                "medicalTreatmentRecordId",
                                                watchRecordIds.filter(
                                                  (id) => id !== record.id,
                                                ),
                                                { shouldDirty: true },
                                              );
                                            }}
                                          >
                                            Remove
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {attachedRecords.length === 0 && (
                            <div className="flex flex-col items-center justify-center gap-2 py-8 text-center">
                              <FileText className="text-muted-foreground/40 h-8 w-8" />
                              <p className="text-muted-foreground text-sm">
                                No records attached yet
                              </p>
                            </div>
                          )}

                          <Separator />

                          {showAttachExistingRecords &&
                            availableRecords.length > 0 && (
                              <div className="space-y-2">
                                <p className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                                  Attach existing records
                                </p>
                                <div className="space-y-1.5">
                                  {availableRecords.map((record) => (
                                    <div
                                      key={record.id}
                                      className="border-border bg-background flex items-center gap-2 rounded-md border px-3 py-2"
                                    >
                                      <FileText className="text-muted-foreground h-4 w-4 shrink-0" />
                                      <div className="min-w-0 flex-1">
                                        <p className="truncate text-sm font-medium">
                                          {trimText(record.name) || "-"}
                                        </p>
                                        <p className="text-muted-foreground text-xs">
                                          {record.status === "Received"
                                            ? `Received${record.receivedDate ? ` ${formatDate(record.receivedDate)}` : ""}`
                                            : `Requested${record.requestDate ? ` ${formatDate(record.requestDate)}` : ""}`}
                                        </p>
                                      </div>
                                      <button
                                        type="button"
                                        disabled={
                                          !canCreateAppointmentConnections
                                        }
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          const currentIds =
                                            appointmentForm.getValues(
                                              "medicalTreatmentRecordId",
                                            ) ?? [];
                                          appointmentForm.setValue(
                                            "medicalTreatmentRecordId",
                                            Array.from(
                                              new Set([
                                                ...currentIds,
                                                record.id,
                                              ]),
                                            ),
                                            { shouldDirty: true },
                                          );
                                        }}
                                        className="text-muted-foreground hover:text-foreground shrink-0 transition-colors"
                                      >
                                        <Plus className="h-4 w-4" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                          {showAttachExistingRecords ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setShowAttachExistingRecords(false);
                              }}
                            >
                              Done
                            </Button>
                          ) : (
                            <div className="flex items-center gap-2">
                              {availableRecords.length > 0 && (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="gap-1.5"
                                  onClick={() => {
                                    setShowAttachExistingRecords(true);
                                  }}
                                >
                                  <Link2 className="h-4 w-4" />
                                  Attach existing
                                </Button>
                              )}
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="gap-1.5"
                                onClick={() => {
                                  setNewRecordTempId(
                                    `${PENDING_FILE_STAGING_PREFIX}${Date.now()}`,
                                  );
                                  setShowNewRecordForm(true);
                                }}
                              >
                                <Upload className="h-4 w-4" />
                                Add new
                              </Button>
                            </div>
                          )}
                        </div>
                      )
                    ) : billsNotApplicable || watchBillNotApplicable ? (
                      <p className="text-muted-foreground text-sm">
                        Bills are marked as not applicable for this{" "}
                        {billsNotApplicable ? "treatment" : "appointment"}.
                      </p>
                    ) : showNewBillForm ? (
                      <div
                        className="space-y-4"
                        onKeyDown={(event) => {
                          if (
                            event.key === "Enter" &&
                            !(event.target instanceof HTMLTextAreaElement)
                          ) {
                            event.preventDefault();
                          }
                        }}
                      >
                        {attachedBills.length > 0 && (
                          <>
                            <div className="space-y-1.5">
                              {attachedBills.map((bill) => {
                                const isAttachedBillReceived =
                                  bill.recordStatus === "Received";
                                const amount = Number(bill.originalBill ?? 0);
                                const formattedAmount = new Intl.NumberFormat(
                                  "en-US",
                                  {
                                    style: "currency",
                                    currency: "USD",
                                  },
                                ).format(Number.isFinite(amount) ? amount : 0);
                                return (
                                  <div
                                    key={bill.id}
                                    className="border-border bg-background flex items-center gap-2 rounded-md border px-3 py-2"
                                  >
                                    <FileText className="text-muted-foreground h-4 w-4 shrink-0" />
                                    <div className="min-w-0 flex-1">
                                      <div className="flex items-center gap-2">
                                        <p className="truncate text-sm font-medium">
                                          {trimText(bill.recordLabel) ||
                                            (isAttachedBillReceived
                                              ? formattedAmount
                                              : "Bill")}
                                        </p>
                                        {isAiGeneratedBill(bill) ? (
                                          <TooltipWrapper message="AI generated bill">
                                            <span
                                              className="inline-flex shrink-0"
                                              aria-label="AI generated bill"
                                            >
                                              <Sparkles className="h-4 w-4 text-blue-500" />
                                            </span>
                                          </TooltipWrapper>
                                        ) : null}
                                      </div>
                                      <p className="text-muted-foreground text-xs">
                                        {bill.recordStatus === "Received"
                                          ? `Received${bill.recordReceivedDate ? ` ${formatDate(bill.recordReceivedDate)}` : ""}`
                                          : `Requested${bill.recordRequestDate ? ` ${formatDate(bill.recordRequestDate)}` : ""}`}
                                        {isAttachedBillReceived &&
                                          ` • ${formattedAmount}`}
                                      </p>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            <Separator />
                          </>
                        )}
                        <p className="text-sm font-semibold">Upload new bill</p>

                        {newBill.recordStatus === "Received" && (
                          <InlineTextField
                            label="Record name"
                            value={newBill.recordLabel}
                            error={newBillErrors.recordLabel}
                            onChange={(value) => {
                              clearNewBillFieldError("recordLabel");
                              setNewBill((prev) => ({
                                ...prev,
                                recordLabel: value,
                              }));
                            }}
                          />
                        )}

                        <InlineSelectField
                          label="Status"
                          options={
                            MEDICAL_TREATMENT_RECORD_SELECTABLE_STATUS_OPTIONS
                          }
                          value={newBill.recordStatus}
                          error={newBillErrors.recordStatus}
                          onChange={(value) => {
                            clearNewBillFieldError("recordStatus");
                            clearNewBillFieldError("recordRequestDate");
                            clearNewBillFieldError("recordReceivedDate");
                            clearNewBillFieldError("recordRejectedDate");
                            clearNewBillFieldError("originalBill");
                            setNewBill((prev) => ({
                              ...prev,
                              recordStatus: value,
                              recordRequestDate:
                                ["Requested", "Received", "Rejected"].includes(
                                  value,
                                ) && !prev.recordRequestDate
                                  ? today
                                  : prev.recordRequestDate,
                              recordReceivedDate:
                                value === "Received" && !prev.recordReceivedDate
                                  ? today
                                  : prev.recordReceivedDate,
                              recordRejectedDate:
                                value === "Rejected" && !prev.recordRejectedDate
                                  ? today
                                  : prev.recordRejectedDate,
                              ...(!canSetMedicalTreatmentBillLien(value)
                                ? { lien: "" }
                                : {}),
                            }));
                            if (value !== "Received" && newBillTempId) {
                              clearStagedItemFiles(newBillTempId, "bill");
                            }
                          }}
                        />

                        {/* Request Date */}
                        {["Requested", "Received", "Rejected"].includes(
                          newBill.recordStatus,
                        ) && (
                          <InlineDateField
                            label="Request date"
                            required
                            value={newBill.recordRequestDate}
                            error={newBillErrors.recordRequestDate}
                            onChange={(value) => {
                              clearNewBillFieldError("recordRequestDate");
                              setNewBill((prev) => ({
                                ...prev,
                                recordRequestDate: value,
                              }));
                            }}
                          />
                        )}

                        {/* Received Date */}
                        {newBill.recordStatus === "Received" && (
                          <InlineDateField
                            label="Date received"
                            required
                            value={newBill.recordReceivedDate}
                            error={newBillErrors.recordReceivedDate}
                            onChange={(value) => {
                              clearNewBillFieldError("recordReceivedDate");
                              setNewBill((prev) => ({
                                ...prev,
                                recordReceivedDate: value,
                              }));
                            }}
                          />
                        )}

                        {newBill.recordStatus === "Rejected" && (
                          <InlineDateField
                            label="Rejected date"
                            required
                            value={newBill.recordRejectedDate}
                            error={newBillErrors.recordRejectedDate}
                            onChange={(value) => {
                              clearNewBillFieldError("recordRejectedDate");
                              setNewBill((prev) => ({
                                ...prev,
                                recordRejectedDate: value,
                              }));
                            }}
                          />
                        )}

                        {canSetMedicalTreatmentBillLien(
                          newBill.recordStatus,
                        ) && (
                          <InlineSelectField
                            label="Lien"
                            options={MEDICAL_TREATMENT_LIEN_STATUS_OPTIONS}
                            value={newBill.lien}
                            clearable
                            onChange={(value) => {
                              setNewBill((prev) => ({
                                ...prev,
                                lien: value,
                              }));
                            }}
                          />
                        )}

                        {newBill.recordStatus === "Received" && (
                          <div className="border-border space-y-3 rounded-md border p-3">
                            <p className="text-sm font-medium">
                              Billing details
                            </p>
                            <div className="grid grid-cols-2 gap-3">
                              <InlineCurrencyField
                                label="Original bill"
                                value={newBill.originalBill}
                                error={newBillErrors.originalBill}
                                onChange={(value) => {
                                  clearNewBillFieldError("originalBill");
                                  setNewBill((prev) => ({
                                    ...prev,
                                    originalBill: value,
                                  }));
                                }}
                              />
                              <InlineCurrencyField
                                label="Goal amount"
                                value={newBill.goalAmount}
                                onChange={(value) => {
                                  setNewBill((prev) => ({
                                    ...prev,
                                    goalAmount: value,
                                  }));
                                }}
                              />
                              <InlineCurrencyField
                                label="Adjusted bill"
                                value={newBill.adjustedBill}
                                onChange={(value) => {
                                  setNewBill((prev) => ({
                                    ...prev,
                                    adjustedBill: value,
                                  }));
                                }}
                              />
                              <InlineCurrencyField
                                label="Negotiated amount"
                                value={newBill.negotiatedAmount}
                                onChange={(value) => {
                                  setNewBill((prev) => ({
                                    ...prev,
                                    negotiatedAmount: value,
                                  }));
                                }}
                              />
                            </div>

                            {/* Payments */}
                            {(() => {
                              const newBillTotalPaid =
                                Number(newBill.clientPaid || 0) +
                                Number(newBill.insurancePaid || 0) +
                                Number(newBill.medpayPipPaid || 0);
                              const newBillTotal = Number(
                                newBill.negotiatedAmount ||
                                  newBill.adjustedBill ||
                                  newBill.originalBill ||
                                  0,
                              );
                              const newBillBalance =
                                newBillTotal - newBillTotalPaid;
                              const fmtCurrencyNew = (v: number) =>
                                new Intl.NumberFormat("en-US", {
                                  style: "currency",
                                  currency: "USD",
                                }).format(v);
                              return (
                                <>
                                  <div className="space-y-2">
                                    <p className="text-xs font-medium">
                                      Payments{" "}
                                      <span className="text-muted-foreground">
                                        ({fmtCurrencyNew(newBillTotalPaid)})
                                      </span>
                                    </p>
                                    <div className="grid grid-cols-2 gap-3">
                                      <InlineCurrencyField
                                        label="Client paid"
                                        value={newBill.clientPaid}
                                        onChange={(value) => {
                                          setNewBill((prev) => ({
                                            ...prev,
                                            clientPaid: value,
                                          }));
                                        }}
                                      />
                                      <InlineCurrencyField
                                        label="Insurance paid"
                                        value={newBill.insurancePaid}
                                        onChange={(value) => {
                                          setNewBill((prev) => ({
                                            ...prev,
                                            insurancePaid: value,
                                          }));
                                        }}
                                      />
                                      <InlineCurrencyField
                                        label="Medpay/PIP paid"
                                        value={newBill.medpayPipPaid}
                                        onChange={(value) => {
                                          setNewBill((prev) => ({
                                            ...prev,
                                            medpayPipPaid: value,
                                          }));
                                        }}
                                      />
                                    </div>
                                  </div>
                                  <div className="border-border flex items-center justify-between border-t pt-2">
                                    <p className="text-sm">
                                      Outstanding balance
                                    </p>
                                    <p className="text-foreground font-semibold">
                                      {fmtCurrencyNew(newBillBalance)}
                                    </p>
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        )}

                        {newBill.recordStatus === "Received" &&
                          newBillTempId && (
                            <ExistingItemFileReferences
                              itemId={newBillTempId}
                              itemType="bill"
                            />
                          )}

                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              if (newBillTempId) {
                                clearStagedItemFiles(newBillTempId, "bill");
                              }
                              resetNewBillForm();
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {attachedBills.length > 0 && (
                          <div className="space-y-1.5">
                            {attachedBills.map((bill) => {
                              const isExpanded = expandedBillId === bill.id;
                              const billDraft = billDrafts[bill.id] ?? {};
                              const billErrors = billFieldErrors[bill.id] ?? {};
                              const fmtCurrency = (v: number) =>
                                new Intl.NumberFormat("en-US", {
                                  style: "currency",
                                  currency: "USD",
                                }).format(v);
                              const mergedBill = {
                                ...bill,
                                ...billDraft,
                              };
                              const effectiveBillStatus =
                                mergedBill.recordStatus;
                              const isBillReceived =
                                effectiveBillStatus === "Received";
                              const draftBillTotal = Number(
                                mergedBill.negotiatedAmount ||
                                  mergedBill.adjustedBill ||
                                  mergedBill.originalBill ||
                                  0,
                              );
                              const draftTotalPaid =
                                Number(mergedBill.clientPaid || 0) +
                                Number(mergedBill.insurancePaid || 0) +
                                Number(mergedBill.medpayPipPaid || 0);
                              const draftBalance =
                                draftBillTotal - draftTotalPaid;

                              return (
                                <div
                                  key={bill.id}
                                  className="border-border bg-background overflow-hidden rounded-md border"
                                >
                                  <button
                                    type="button"
                                    className="hover:bg-muted/30 flex w-full items-center gap-2 px-3 py-2 transition-colors"
                                    onClick={() => {
                                      if (isExpanded) {
                                        setExpandedBillId(null);
                                      } else {
                                        setExpandedBillId(bill.id);
                                      }
                                    }}
                                  >
                                    <FileText className="text-muted-foreground h-4 w-4 shrink-0" />
                                    <div className="min-w-0 flex-1 text-left">
                                      <div className="flex items-center gap-2">
                                        <p className="truncate text-sm font-medium">
                                          {trimText(bill.recordLabel) ||
                                            (isBillReceived
                                              ? fmtCurrency(draftBillTotal)
                                              : "Bill")}
                                        </p>
                                        {isAiGeneratedBill(bill) ? (
                                          <TooltipWrapper message="AI generated bill">
                                            <span
                                              className="inline-flex shrink-0"
                                              aria-label="AI generated bill"
                                            >
                                              <Sparkles className="h-4 w-4 text-blue-500" />
                                            </span>
                                          </TooltipWrapper>
                                        ) : null}
                                      </div>
                                      <p className="text-muted-foreground text-xs">
                                        {effectiveBillStatus === "Received"
                                          ? `Received${mergedBill.recordReceivedDate ? ` ${formatDate(mergedBill.recordReceivedDate)}` : ""}`
                                          : `Requested${mergedBill.recordRequestDate ? ` ${formatDate(mergedBill.recordRequestDate)}` : ""}`}
                                      </p>
                                    </div>
                                    {isBillReceived && (
                                      <div className="flex items-center gap-3 text-xs">
                                        <div className="text-right">
                                          <p className="text-muted-foreground">
                                            Total
                                          </p>
                                          <p className="text-foreground font-medium">
                                            {fmtCurrency(draftBillTotal)}
                                          </p>
                                        </div>
                                        <div className="text-right">
                                          <p className="text-muted-foreground">
                                            Outstanding
                                          </p>
                                          <p className="text-foreground font-medium">
                                            {fmtCurrency(draftBalance)}
                                          </p>
                                        </div>
                                      </div>
                                    )}
                                    <ChevronDown
                                      className={cn(
                                        "text-muted-foreground h-4 w-4 shrink-0 transition-transform",
                                        isExpanded && "rotate-180",
                                      )}
                                    />
                                  </button>
                                  {isExpanded && (
                                    <div className="border-border space-y-4 border-t px-3 py-3">
                                      {effectiveBillStatus === "Received" && (
                                        <InlineTextField
                                          label="Record name"
                                          value={
                                            billDraft.recordLabel ??
                                            bill.recordLabel
                                          }
                                          error={billErrors.recordLabel}
                                          onChange={(value) => {
                                            clearBillFieldError(
                                              bill.id,
                                              "recordLabel",
                                            );
                                            setBillDrafts((prev) => ({
                                              ...prev,
                                              [bill.id]: {
                                                ...prev[bill.id],
                                                recordLabel: value,
                                              },
                                            }));
                                          }}
                                        />
                                      )}
                                      <InlineSelectField
                                        label="Status"
                                        options={
                                          MEDICAL_TREATMENT_RECORD_SELECTABLE_STATUS_OPTIONS
                                        }
                                        value={
                                          billDraft.recordStatus ??
                                          bill.recordStatus
                                        }
                                        error={billErrors.recordStatus}
                                        onChange={(value) => {
                                          clearBillFieldError(
                                            bill.id,
                                            "recordStatus",
                                          );
                                          clearBillFieldError(
                                            bill.id,
                                            "recordRequestDate",
                                          );
                                          clearBillFieldError(
                                            bill.id,
                                            "recordReceivedDate",
                                          );
                                          clearBillFieldError(
                                            bill.id,
                                            "recordRejectedDate",
                                          );
                                          clearBillFieldError(
                                            bill.id,
                                            "originalBill",
                                          );
                                          setBillDrafts((prev) => {
                                            const existingDraft =
                                              prev[bill.id] ?? {};
                                            return {
                                              ...prev,
                                              [bill.id]: {
                                                ...existingDraft,
                                                recordStatus: value,
                                                recordRequestDate:
                                                  [
                                                    "Requested",
                                                    "Received",
                                                    "Rejected",
                                                  ].includes(value) &&
                                                  !(
                                                    existingDraft.recordRequestDate ??
                                                    bill.recordRequestDate
                                                  )
                                                    ? today
                                                    : existingDraft.recordRequestDate,
                                                recordReceivedDate:
                                                  value === "Received" &&
                                                  !(
                                                    existingDraft.recordReceivedDate ??
                                                    bill.recordReceivedDate
                                                  )
                                                    ? today
                                                    : existingDraft.recordReceivedDate,
                                                ...(!canSetMedicalTreatmentBillLien(
                                                  value,
                                                )
                                                  ? {
                                                      lien: "",
                                                    }
                                                  : {}),
                                              },
                                            };
                                          });
                                          if (value !== "Received") {
                                            setExistingBillFiles((prev) => ({
                                              ...prev,
                                              [bill.id]: [],
                                            }));
                                          }
                                        }}
                                      />

                                      {/* Request Date - conditional */}
                                      {[
                                        "Requested",
                                        "Received",
                                        "Rejected",
                                      ].includes(effectiveBillStatus) && (
                                        <InlineDateField
                                          label="Request date"
                                          required
                                          value={
                                            billDraft.recordRequestDate ??
                                            bill.recordRequestDate ??
                                            ""
                                          }
                                          error={billErrors.recordRequestDate}
                                          onChange={(value) => {
                                            clearBillFieldError(
                                              bill.id,
                                              "recordRequestDate",
                                            );
                                            setBillDrafts((prev) => ({
                                              ...prev,
                                              [bill.id]: {
                                                ...prev[bill.id],
                                                recordRequestDate: value,
                                              },
                                            }));
                                          }}
                                        />
                                      )}

                                      {/* Received Date - conditional */}
                                      {effectiveBillStatus === "Received" && (
                                        <InlineDateField
                                          label="Date received"
                                          required
                                          value={
                                            billDraft.recordReceivedDate ??
                                            bill.recordReceivedDate ??
                                            ""
                                          }
                                          error={billErrors.recordReceivedDate}
                                          onChange={(value) => {
                                            clearBillFieldError(
                                              bill.id,
                                              "recordReceivedDate",
                                            );
                                            setBillDrafts((prev) => ({
                                              ...prev,
                                              [bill.id]: {
                                                ...prev[bill.id],
                                                recordReceivedDate: value,
                                              },
                                            }));
                                          }}
                                        />
                                      )}

                                      {canSetMedicalTreatmentBillLien(
                                        effectiveBillStatus,
                                      ) && (
                                        <InlineSelectField
                                          label="Lien"
                                          options={
                                            MEDICAL_TREATMENT_LIEN_STATUS_OPTIONS
                                          }
                                          value={
                                            billDraft.lien ?? bill.lien ?? ""
                                          }
                                          clearable
                                          onChange={(value) => {
                                            setBillDrafts((prev) => ({
                                              ...prev,
                                              [bill.id]: {
                                                ...prev[bill.id],
                                                lien: value,
                                              },
                                            }));
                                          }}
                                        />
                                      )}

                                      {effectiveBillStatus === "Received" && (
                                        <div className="border-border space-y-3 rounded-md border p-3">
                                          <p className="text-sm font-medium">
                                            Billing details
                                          </p>
                                          <div className="grid grid-cols-2 gap-3">
                                            <InlineCurrencyField
                                              label="Original bill"
                                              required
                                              value={
                                                billDraft.originalBill ??
                                                bill.originalBill ??
                                                ""
                                              }
                                              error={billErrors.originalBill}
                                              onChange={(value) => {
                                                clearBillFieldError(
                                                  bill.id,
                                                  "originalBill",
                                                );
                                                setBillDrafts((prev) => ({
                                                  ...prev,
                                                  [bill.id]: {
                                                    ...prev[bill.id],
                                                    originalBill:
                                                      value === ""
                                                        ? null
                                                        : Number(value),
                                                  },
                                                }));
                                              }}
                                            />
                                            <InlineCurrencyField
                                              label="Goal amount"
                                              value={
                                                billDraft.goalAmount ??
                                                bill.goalAmount ??
                                                ""
                                              }
                                              onChange={(value) => {
                                                setBillDrafts((prev) => ({
                                                  ...prev,
                                                  [bill.id]: {
                                                    ...prev[bill.id],
                                                    goalAmount:
                                                      value === ""
                                                        ? null
                                                        : Number(value),
                                                  },
                                                }));
                                              }}
                                            />
                                            <InlineCurrencyField
                                              label="Adjusted bill"
                                              value={
                                                billDraft.adjustedBill ??
                                                bill.adjustedBill ??
                                                ""
                                              }
                                              onChange={(value) => {
                                                setBillDrafts((prev) => ({
                                                  ...prev,
                                                  [bill.id]: {
                                                    ...prev[bill.id],
                                                    adjustedBill:
                                                      value === ""
                                                        ? null
                                                        : Number(value),
                                                  },
                                                }));
                                              }}
                                            />
                                            <InlineCurrencyField
                                              label="Negotiated amount"
                                              value={
                                                billDraft.negotiatedAmount ??
                                                bill.negotiatedAmount ??
                                                ""
                                              }
                                              onChange={(value) => {
                                                setBillDrafts((prev) => ({
                                                  ...prev,
                                                  [bill.id]: {
                                                    ...prev[bill.id],
                                                    negotiatedAmount:
                                                      value === ""
                                                        ? null
                                                        : Number(value),
                                                  },
                                                }));
                                              }}
                                            />
                                          </div>

                                          {/* Payments */}
                                          <div className="space-y-2">
                                            <p className="text-xs font-medium">
                                              Payments{" "}
                                              <span className="text-muted-foreground">
                                                ({fmtCurrency(draftTotalPaid)})
                                              </span>
                                            </p>
                                            <div className="grid grid-cols-2 gap-3">
                                              <InlineCurrencyField
                                                label="Client paid"
                                                value={
                                                  billDraft.clientPaid ??
                                                  bill.clientPaid ??
                                                  ""
                                                }
                                                onChange={(value) => {
                                                  setBillDrafts((prev) => ({
                                                    ...prev,
                                                    [bill.id]: {
                                                      ...prev[bill.id],
                                                      clientPaid:
                                                        value === ""
                                                          ? null
                                                          : Number(value),
                                                    },
                                                  }));
                                                }}
                                              />
                                              <InlineCurrencyField
                                                label="Insurance paid"
                                                value={
                                                  billDraft.insurancePaid ??
                                                  bill.insurancePaid ??
                                                  ""
                                                }
                                                onChange={(value) => {
                                                  setBillDrafts((prev) => ({
                                                    ...prev,
                                                    [bill.id]: {
                                                      ...prev[bill.id],
                                                      insurancePaid:
                                                        value === ""
                                                          ? null
                                                          : Number(value),
                                                    },
                                                  }));
                                                }}
                                              />
                                              <InlineCurrencyField
                                                label="Medpay/PIP paid"
                                                value={
                                                  billDraft.medpayPipPaid ??
                                                  bill.medpayPipPaid ??
                                                  ""
                                                }
                                                onChange={(value) => {
                                                  setBillDrafts((prev) => ({
                                                    ...prev,
                                                    [bill.id]: {
                                                      ...prev[bill.id],
                                                      medpayPipPaid:
                                                        value === ""
                                                          ? null
                                                          : Number(value),
                                                    },
                                                  }));
                                                }}
                                              />
                                            </div>
                                          </div>

                                          {/* Outstanding Balance */}
                                          <div className="border-border flex items-center justify-between border-t pt-2">
                                            <p className="text-sm">
                                              Outstanding balance
                                            </p>
                                            <p className="text-foreground font-semibold">
                                              {fmtCurrency(draftBalance)}
                                            </p>
                                          </div>
                                        </div>
                                      )}

                                      {isBillReceived && (
                                        <ExistingItemFileReferences
                                          itemId={bill.id}
                                          itemType="bill"
                                        />
                                      )}

                                      <div className="border-border border-t pt-3">
                                        <button
                                          type="button"
                                          disabled={
                                            !canDeleteAppointmentConnections
                                          }
                                          className="text-destructive hover:text-destructive/80 text-sm transition-colors"
                                          onClick={() => {
                                            appointmentForm.setValue(
                                              "medicalTreatmentBillId",
                                              watchBillIds.filter(
                                                (id) => id !== bill.id,
                                              ),
                                              { shouldDirty: true },
                                            );
                                          }}
                                        >
                                          Remove
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {attachedBills.length === 0 && (
                          <div className="flex flex-col items-center justify-center gap-2 py-8 text-center">
                            <FileText className="text-muted-foreground/40 h-8 w-8" />
                            <p className="text-muted-foreground text-sm">
                              No bills attached yet
                            </p>
                          </div>
                        )}

                        <Separator />

                        {showAttachExistingBills &&
                          availableBills.length > 0 && (
                            <div className="space-y-2">
                              <p className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                                Attach existing bills
                              </p>
                              <div className="space-y-1.5">
                                {availableBills.map((bill) => {
                                  const isAvailableBillReceived =
                                    bill.recordStatus === "Received";
                                  const amount = Number(bill.originalBill ?? 0);
                                  const formattedAmount = new Intl.NumberFormat(
                                    "en-US",
                                    {
                                      style: "currency",
                                      currency: "USD",
                                    },
                                  ).format(
                                    Number.isFinite(amount) ? amount : 0,
                                  );
                                  return (
                                    <div
                                      key={bill.id}
                                      className="border-border bg-background flex items-center gap-2 rounded-md border px-3 py-2"
                                    >
                                      <FileText className="text-muted-foreground h-4 w-4 shrink-0" />
                                      <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                          <p className="truncate text-sm font-medium">
                                            {trimText(bill.recordLabel) ||
                                              (isAvailableBillReceived
                                                ? formattedAmount
                                                : "Bill")}
                                          </p>
                                          {isAiGeneratedBill(bill) ? (
                                            <TooltipWrapper message="AI generated bill">
                                              <span
                                                className="inline-flex shrink-0"
                                                aria-label="AI generated bill"
                                              >
                                                <Sparkles className="h-4 w-4 text-blue-500" />
                                              </span>
                                            </TooltipWrapper>
                                          ) : null}
                                        </div>
                                        <p className="text-muted-foreground text-xs">
                                          {bill.recordStatus === "Received"
                                            ? `Received${bill.recordReceivedDate ? ` ${formatDate(bill.recordReceivedDate)}` : ""}`
                                            : `Requested${bill.recordRequestDate ? ` ${formatDate(bill.recordRequestDate)}` : ""}`}
                                          {isAvailableBillReceived &&
                                            ` • ${formattedAmount}`}
                                        </p>
                                      </div>
                                      <button
                                        type="button"
                                        disabled={
                                          !canCreateAppointmentConnections
                                        }
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          const currentIds =
                                            appointmentForm.getValues(
                                              "medicalTreatmentBillId",
                                            ) ?? [];
                                          appointmentForm.setValue(
                                            "medicalTreatmentBillId",
                                            Array.from(
                                              new Set([...currentIds, bill.id]),
                                            ),
                                            { shouldDirty: true },
                                          );
                                        }}
                                        className="text-muted-foreground hover:text-foreground shrink-0 transition-colors"
                                      >
                                        <Plus className="h-4 w-4" />
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                        {showAttachExistingBills ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setShowAttachExistingBills(false);
                            }}
                          >
                            Done
                          </Button>
                        ) : (
                          <div className="flex items-center gap-2">
                            {availableBills.length > 0 && (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="gap-1.5"
                                onClick={() => {
                                  setShowAttachExistingBills(true);
                                }}
                              >
                                <Link2 className="h-4 w-4" />
                                Attach existing
                              </Button>
                            )}
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="gap-1.5"
                              onClick={() => {
                                setNewBillTempId(
                                  `${PENDING_FILE_STAGING_PREFIX}${Date.now()}`,
                                );
                                setShowNewBillForm(true);
                              }}
                            >
                              <Upload className="h-4 w-4" />
                              Add new
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="border-border flex flex-shrink-0 items-center justify-between border-t px-6 py-4">
                <div>
                  {appointmentWithConnections &&
                    hasActionPermission(
                      ACTION_PERMISSION_KEYS.matterMedicalTreatmentAppointment,
                      ACTION_PERMISSION_VALUES.delete,
                    ) && (
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => {
                          setIsDeleting(true);
                        }}
                      >
                        Delete
                      </Button>
                    )}
                </div>
                <div className="flex items-center gap-2">
                  <FormCancelButton
                    setOpen={setOpen}
                    isDirty={hasUnsavedChanges}
                  />
                  <FormSubmitButton
                    variant="default"
                    loading={isFormLoading}
                    overrideDisabled
                    disabled={Boolean(initialData) || !hasUnsavedChanges}
                    title="Save changes"
                  />
                </div>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <AddOrEditFileReferenceSlideover
        open={Boolean(activeFileReferenceTarget)}
        setOpen={(nextOpen) => {
          if (!nextOpen) {
            setActiveFileReferenceTarget(null);
          }
        }}
        matter={matter}
        handleCreateMatterFileReference={(selectedFiles) => {
          if (!activeFileReferenceTarget) {
            return;
          }

          stageExistingItemFileReferences(
            activeFileReferenceTarget.itemId,
            activeFileReferenceTarget.itemType,
            selectedFiles,
          );
        }}
        isCreating={false}
      />

      <ActionConfirmationModal
        open={confirmClose}
        setOpen={setConfirmClose}
        title="Unsaved changes"
        buttonText="Confirm"
        description={SLIDEOVER_CONFIRM_CLOSE_DESCRIPTION}
        afterActionFn={() => {
          setConfirmClose(false);
          setOpen(false);
        }}
      />

      <ActionConfirmationModal
        open={isDeleting}
        setOpen={setIsDeleting}
        title="Delete treatment date"
        description="Are you sure you want to delete this appointment? This action cannot be undone."
        buttonText="Delete"
        afterActionFn={() => {
          if (selectedAppointment) {
            deleteAppointment.mutate({
              matterId: matter.id,
              id: selectedAppointment.id,
            });
            setOpen(false);
          }
        }}
      />
    </>
  );
};
